import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Toolbar as MuiToolbar, TextField, Grid } from "@mui/material";
import ServicesTable from "../components/ServicesTable";
import Pagination from "../components/Pagination";
import ImageGalleryDropdown from "../components/ImageGalleryDropdown";
import { useServicesState } from "../hooks/useServicesState";
import { ACTIONS } from '../reducers/listActions';
import RecordNavigator from "../components/RecordNavigator";
import { useRefresh } from "../context/RefreshContext";

const petServicesColumns = [
    { id: 'redirect', label: '', sortable: false },
    { id: 'date', label: 'Date', sortable: true },
    { id: 'time', label: 'Time', sortable: false },
    { id: 'service', label: 'Service', sortable: false },
    { id: 'amount', label: 'Amount', sortable: false },
    { id: 'groomer', label: 'Groomer', sortable: false },
    { id: 'comments', label: '', sortable: false },
    { id: 'imageUrl', label: '', sortable: false },
];

const PetDetails = () => {
    const { id } = useParams(); // route: /pets/:id
    const navigate = useNavigate();
    const [pet, setPet] = useState(null);
    const location = useLocation();
    const [pets, setPets] = useState(location.state?.records || []);
    const [editedPet, setEditedPet] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const { state, dispatch, setPage, setRowsPerPage, handleSortRequest } = useServicesState();
    const { services, order, orderBy, page, rowsPerPage, totalServices } = state;
    const { triggerRefresh } = useRefresh();

    useEffect(() => {
        async function fetchPetDetails() {
            try {
                const token = localStorage.getItem("token");
                if (!token) throw new Error("No token found");
                const response = await api.get(`/pets/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const petData = response.data;
                setPet(petData);
                setEditedPet(petData);
            } catch (error) {
                console.error("Error fetching pet details:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchPetDetails();
    }, [id, triggerRefresh]);

    useEffect(() => {
        if (!pets.length) {
          async function fetchAllPets() {
            try {
              const token = localStorage.getItem("token");
              const response = await api.get(`/customers/${pet.owner.id}/pets`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setPets(response.data.data);
            } catch (error) {
              console.error("Error fetching pets:", error);
            }
          }
          fetchAllPets();
        }
      }, [pets, pet]);

    useEffect(() => {
        async function fetchPetServices() {
            try {
                const token = localStorage.getItem("token");
                const response = await api.get(`/pets/${id}/services`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        page: page + 1, // assuming backend pages are 1-indexed
                        limit: rowsPerPage,
                        sortBy: orderBy,
                        order: order,
                    },
                });
                dispatch({
                    type: ACTIONS.FETCH_SUCCESS,
                    payload: {
                        services: response.data.data,
                        total: response.data.total,
                    },
                });
            } catch (error) {
                dispatch({ type: ACTIONS.FETCH_FAILURE, payload: "Failed to fetch services." });
            }
        }
        fetchPetServices();
    }, [id, page, rowsPerPage, order, orderBy, dispatch]);

    // Update editedPet whenever pet data changes
    useEffect(() => {
        if (pet) {
            setEditedPet(pet);
        }
    }, [pet]);

    if (loading) {
        return <Typography>Loading pet details...</Typography>;
    }
    if (!pet) {
        return <Typography>Pet not found.</Typography>;
    }

    // Handle input changes in the edit form
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedPet({ ...editedPet, [name]: value });
    };

    // Toggle edit mode or save changes if already editing
    const handleEditClick = async () => {
        if (isEditing) {
            try {
                const token = localStorage.getItem("token");
                const updatePayload = {
                    name: editedPet.name,
                    breed: editedPet.breed,
                    sex: editedPet.sex,
                    weight: editedPet.weight,
                    color: editedPet.color,
                    age: editedPet.age,
                    medissues: editedPet.medissues,
                    comments: editedPet.comments,
                };
                //console.log("Sending update payload:", updatePayload);
                const response = await api.put(`/pets/${id}`, updatePayload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                console.log("PUT response data:", response.data);
                if (response.data) {
                    setPet(prev => ({
                        ...response.data,
                        bucket: prev.bucket || response.data.bucket,
                    }));

                    setEditedPet(prev => ({
                        ...response.data,
                        bucket: prev.bucket || response.data.bucket,
                    }));
                    triggerRefresh();
                    setIsEditing(false);
                } else {
                    console.error("No updated data received from API.");
                }
            } catch (error) {
                console.error("Error saving changes:", error.response ? error.response.data : error);
            }
        } else {
            setIsEditing(true);
        }
    };

    // Delete pet with confirmation
    const handleDeleteClick = () => {
        if (window.confirm("Are you sure you want to delete this pet? This action cannot be undone.")) {
            const token = localStorage.getItem("token");
            api
                .delete(`/pets/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then(() => {
                    navigate(`/customers/${pet.owner.id}`);
                })
                .catch((error) => {
                    console.error("Error deleting pet:", error);
                });
        }
    };

    const disabledInputStyles = {
        "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "black",
        },
    };

    return (
        <>
            <MuiToolbar />
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Paper sx={{ p: 2 }}>
                    {/* Header: Customer Name > Pet Details */}
                    <Typography variant="h5" gutterBottom sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>
                            <Link to={`/customers/${pet.owner.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {pet.owner.firstName} {pet.owner.lastName}
                            </Link> {" > "} Pet Details
                        </span>
                        <RecordNavigator records={pets} currentId={id} basePath="/pets" />
                    </Typography>

                    {/* Pet Details Section */}
                    <Box sx={{ maxWidth: '80%', mx: 'auto' }}>
                        <Grid container spacing={2} justifyContent="center">
                            {/* Row 1: Name, Breed, Sex, Weight, Color, Age */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Name"
                                    name="name"
                                    value={editedPet.name || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Breed"
                                    name="breed"
                                    value={editedPet.breed || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                                <TextField
                                    label="Sex"
                                    name="sex"
                                    value={editedPet.sex || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="Weight"
                                    name="weight"
                                    value={editedPet.weight || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="Color"
                                    name="color"
                                    value={editedPet.color || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                                <TextField
                                    label="Age"
                                    name="age"
                                    value={editedPet.age || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>

                            {/* Row 2: Medical Issues, Comments, Photo Gallery Icon */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Medical Issues"
                                    name="medissues"
                                    value={editedPet.medissues || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    multiline
                                    rows={2}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="Comments"
                                    name="comments"
                                    value={editedPet.comments || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    multiline
                                    rows={2}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={1} sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ImageGalleryDropdown
                                    images={editedPet.bucket?.images || []}
                                    onUploadSuccess={(newImageData) => {
                                        setEditedPet(prev => ({
                                            ...prev,
                                            bucket: {
                                                ...prev.bucket,
                                                images: [...(prev.bucket?.images || []), newImageData]
                                            }
                                        }));
                                    }}
                                    onDelete={(imageId) => {
                                        api.delete(`/images/${imageId}`)
                                            .then(() => {
                                                setEditedPet(prev => ({
                                                    ...prev,
                                                    bucket: {
                                                        ...prev.bucket,
                                                        images: prev.bucket.images.filter(img => img.id !== imageId)
                                                    }
                                                }));
                                            })
                                            .catch(error => {
                                                console.error("Error deleting image:", error);
                                            });
                                    }}
                                    petId={pet.id}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', maxWidth: '60%', mx: 'auto' }}>
                        <Button variant="contained" color="primary" onClick={handleEditClick}>
                            {isEditing ? "Save Changes" : "Edit Pet"}
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleDeleteClick}>
                            Delete Pet
                        </Button>
                    </Box>
                </Paper>

                {/* Services Table Panel */}
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Services
                    </Typography>
                    {services.length > 0 ? (
                        <>
                            {services.length > rowsPerPage && (
                                <Pagination
                                    count={services.length}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={(_, newPage) => setPage(newPage)}
                                />
                            )}
                            <ServicesTable
                                data={services}
                                columns={petServicesColumns}
                                order={order}
                                orderBy={orderBy}
                                onSortRequest={handleSortRequest}
                            />
                            {totalServices > rowsPerPage && (
                                <Pagination
                                    count={totalServices}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={(e, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                                />
                            )}
                        </>
                    ) : (
                        <Typography>No services found.</Typography>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default PetDetails;